import { ArrowRight, TrendingUp, TrendingDown } from 'lucide-react';
import { FactorBreakdown, FactorType } from '../../services/forecasts.service';

interface ForecastFactorBreakdownProps {
  data: FactorBreakdown;
  isLoading: boolean;
}

const FACTOR_TYPE_COLORS: Record<FactorType, { bg: string; bar: string }> = {
  BASE_VOLUME: { bg: 'bg-gray-100', bar: 'bg-gray-500' },
  GROWTH: { bg: 'bg-green-100', bar: 'bg-green-500' },
  EFFICIENCY: { bg: 'bg-blue-100', bar: 'bg-blue-500' },
  EVENT_OUTAGE: { bg: 'bg-red-100', bar: 'bg-red-500' },
  EVENT_PROMOTION: { bg: 'bg-purple-100', bar: 'bg-purple-500' },
  EVENT_LAUNCH: { bg: 'bg-yellow-100', bar: 'bg-yellow-500' },
  EVENT_OTHER: { bg: 'bg-orange-100', bar: 'bg-orange-500' },
};

export function ForecastFactorBreakdown({ data, isLoading }: ForecastFactorBreakdownProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading factor breakdown...</span>
      </div>
    );
  }

  if (!data || data.factors.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
        <p>No factor breakdown available.</p>
        <p className="text-sm">Add assumptions to see how they affect your forecast.</p>
      </div>
    );
  }

  // Calculate the max value for scaling the bars
  const maxVolume = Math.max(data.baseVolume, data.finalVolume, ...data.factors.map(f => f.cumulativeVolume));
  const getBarWidth = (volume: number) => `${(volume / maxVolume) * 100}%`;

  // Calculate overall change
  const overallChange = data.finalVolume - data.baseVolume;
  const overallChangePercent = ((overallChange / data.baseVolume) * 100).toFixed(1);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div>
          <div className="text-sm text-gray-600">Base Volume</div>
          <div className="text-2xl font-bold text-gray-900">{data.baseVolume.toLocaleString()}</div>
        </div>
        <ArrowRight className="w-6 h-6 text-gray-400" />
        <div className="text-center">
          <div className="text-sm text-gray-600">Net Impact</div>
          <div className={`text-lg font-semibold flex items-center justify-center ${overallChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {overallChange >= 0 ? (
              <TrendingUp className="w-5 h-5 mr-1" />
            ) : (
              <TrendingDown className="w-5 h-5 mr-1" />
            )}
            {overallChange >= 0 ? '+' : ''}{overallChange.toLocaleString()} ({overallChangePercent}%)
          </div>
        </div>
        <ArrowRight className="w-6 h-6 text-gray-400" />
        <div className="text-right">
          <div className="text-sm text-gray-600">Final Volume</div>
          <div className="text-2xl font-bold text-blue-600">{data.finalVolume.toLocaleString()}</div>
        </div>
      </div>

      {/* Waterfall breakdown */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700">Factor Breakdown</h4>

        {/* Base volume row */}
        <div className="flex items-center gap-4">
          <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-700">Base Volume</div>
          <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
            <div
              className="h-full bg-gray-500 transition-all duration-500"
              style={{ width: getBarWidth(data.baseVolume) }}
            />
          </div>
          <div className="w-24 text-right text-sm font-medium text-gray-900">
            {data.baseVolume.toLocaleString()}
          </div>
        </div>

        {/* Factor rows */}
        {data.factors.map((factor, index) => {
          const colors = FACTOR_TYPE_COLORS[factor.factorType];
          const isPositive = factor.volumeContribution >= 0;

          return (
            <div key={factor.id} className="flex items-center gap-4">
              <div className="w-32 flex-shrink-0">
                <div className="text-sm font-medium text-gray-700 truncate" title={factor.name}>
                  {factor.name}
                </div>
                <div className={`text-xs ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                  {isPositive ? '+' : ''}{factor.volumeContribution.toLocaleString()}
                  {factor.percentageImpact !== null && ` (${factor.percentageImpact >= 0 ? '+' : ''}${factor.percentageImpact}%)`}
                </div>
              </div>
              <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden relative">
                {/* Previous cumulative bar (lighter) */}
                {index > 0 && (
                  <div
                    className="absolute h-full bg-gray-300 opacity-50"
                    style={{ width: getBarWidth(data.factors[index - 1].cumulativeVolume) }}
                  />
                )}
                {index === 0 && (
                  <div
                    className="absolute h-full bg-gray-300 opacity-50"
                    style={{ width: getBarWidth(data.baseVolume) }}
                  />
                )}
                {/* Current cumulative bar */}
                <div
                  className={`h-full ${colors.bar} transition-all duration-500`}
                  style={{ width: getBarWidth(factor.cumulativeVolume) }}
                />
              </div>
              <div className="w-24 text-right text-sm font-medium text-gray-900">
                {factor.cumulativeVolume.toLocaleString()}
              </div>
            </div>
          );
        })}

        {/* Final volume row */}
        <div className="flex items-center gap-4 pt-2 border-t border-gray-200">
          <div className="w-32 flex-shrink-0 text-sm font-bold text-gray-900">Final Volume</div>
          <div className="flex-1 h-8 bg-blue-100 rounded-lg overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-500"
              style={{ width: getBarWidth(data.finalVolume) }}
            />
          </div>
          <div className="w-24 text-right text-sm font-bold text-blue-600">
            {data.finalVolume.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <div className="w-3 h-3 bg-green-500 rounded" />
          <span>Growth</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <div className="w-3 h-3 bg-blue-500 rounded" />
          <span>Efficiency</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <div className="w-3 h-3 bg-purple-500 rounded" />
          <span>Promotion</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <div className="w-3 h-3 bg-yellow-500 rounded" />
          <span>Launch</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <div className="w-3 h-3 bg-red-500 rounded" />
          <span>Outage</span>
        </div>
      </div>
    </div>
  );
}
